import { useState, useEffect, useRef, useCallback } from 'react';
import { getAudioUrl, DEFAULT_RECITER, RECITERS } from '../services/quranAPI';
import useMediaSession from './useMediaSession';
import { precacheSurahAudio } from '../services/quranSW';

export const REPEAT_MODES = ['none', 'one', 'all'];
export const SPEEDS = [0.75, 1, 1.25, 1.5];

/**
 * useFullAudioPlayer — complete Quran audio engine with:
 * - Sequential playback, repeat modes, speed control
 * - Media Session API (lock-screen / notification panel controls)
 * - Offline detection and SW pre-caching
 * - Recitation mode support (pauses after each verse)
 */
const useFullAudioPlayer = ({ verses = [], reciterId = DEFAULT_RECITER, onVerseEnded, onSurahEnded, chapterName = '', readMode = 'reading' } = {}) => {
  const audioRef         = useRef(null);
  const versesRef        = useRef(verses);
  const reciterRef       = useRef(reciterId);
  const modeRef          = useRef('none');
  const onVerseEndedRef  = useRef(onVerseEnded);
  const onSurahEndedRef  = useRef(onSurahEnded);
  const chapterNameRef   = useRef(chapterName);
  const readModeRef      = useRef(readMode);

  useEffect(() => { versesRef.current = verses; }, [verses]);
  useEffect(() => { reciterRef.current = reciterId; }, [reciterId]);
  useEffect(() => { onVerseEndedRef.current = onVerseEnded; }, [onVerseEnded]);
  useEffect(() => { onSurahEndedRef.current = onSurahEnded; }, [onSurahEnded]);
  useEffect(() => { chapterNameRef.current = chapterName; }, [chapterName]);
  useEffect(() => { readModeRef.current = readMode; }, [readMode]);

  const progressInterval = useRef(null);

  const [currentIdx,  setCurrentIdx]  = useState(null);
  const [playing,     setPlaying]     = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [progress,    setProgress]    = useState(0);
  const [duration,    setDuration]    = useState(0);
  const [elapsed,     setElapsed]     = useState(0);
  const [repeatMode,  setRepeatMode]  = useState('none');
  const [speed,       setSpeed]       = useState(1);
  const [visible,     setVisible]     = useState(false);
  const [exiting,     setExiting]     = useState(false);
  const [isOffline,   setIsOffline]   = useState(() => !navigator.onLine);

  // Sync repeatMode to ref AFTER it's declared (avoids TDZ error)
  useEffect(() => { modeRef.current = repeatMode; }, [repeatMode]);

  // Track network status
  useEffect(() => {
    const go_online  = () => setIsOffline(false);
    const go_offline = () => setIsOffline(true);
    window.addEventListener('online',  go_online);
    window.addEventListener('offline', go_offline);
    return () => { window.removeEventListener('online', go_online); window.removeEventListener('offline', go_offline); };
  }, []);

  const startProgress = useCallback(() => {
    clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      const a = audioRef.current;
      if (!a) return;
      setElapsed(a.currentTime);
      setProgress(a.duration > 0 ? a.currentTime / a.duration : 0);
      setDuration(a.duration || 0);
    }, 250);
  }, []);

  const stopProgress = useCallback(() => clearInterval(progressInterval.current), []);

  const playAtIdx = useCallback((idx) => {
    const vArr = versesRef.current;
    if (idx < 0 || idx >= vArr.length) { setPlaying(false); setCurrentIdx(null); return; }
    const verse = vArr[idx];
    const url = getAudioUrl(verse.verse_key, reciterRef.current);
    if (!url) return;

    setCurrentIdx(idx);
    setLoading(true);
    setVisible(true);
    setExiting(false);

    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    a.pause();
    a.src = url;
    a.playbackRate = speed;
    a.load();

    a.onloadstart = () => setLoading(true);
    a.oncanplay   = () => { setLoading(false); a.play().catch(() => {}); };
    a.onplay      = () => { setPlaying(true); startProgress(); };
    a.onpause     = () => { setPlaying(false); stopProgress(); };
    a.onended     = () => {
      onVerseEndedRef.current?.();
      const mode = modeRef.current;
      const list = versesRef.current;
      const currentReadMode = readModeRef.current;
      setPlaying(false);
      stopProgress();
      
      // In recitation mode, DON'T auto-advance - let the reader component handle timing
      if (currentReadMode === 'recitation') {
        setProgress(1);
        return;
      }
      
      if      (mode === 'one')  { a.currentTime = 0; a.play().catch(() => {}); }
      else if (mode === 'none') { const ni = idx + 1; if (ni < list.length) playAtIdx(ni); else { setProgress(1); onSurahEndedRef.current?.(); } }
      else if (mode === 'all')  { playAtIdx(idx + 1 < list.length ? idx + 1 : 0); }
    };
    a.onerror = () => { setLoading(false); setPlaying(false); };
  }, [speed, startProgress, stopProgress]);

  // Public API
  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    playing ? a.pause() : a.play().catch(() => {});
  }, [playing]);

  const playVerse = useCallback((verseKey) => {
    const idx = versesRef.current.findIndex(v => v.verse_key === verseKey);
    if (idx >= 0) playAtIdx(idx);
  }, [playAtIdx]);

  const playSurah = useCallback((fromIdx = 0) => {
    // Pre-cache next 10 verses audio in background
    const urls = versesRef.current
      .slice(fromIdx, fromIdx + 10)
      .map(v => getAudioUrl(v.verse_key, reciterRef.current))
      .filter(Boolean);
    precacheSurahAudio(urls);
    playAtIdx(fromIdx);
  }, [playAtIdx]);

  const playPrev = useCallback(() => {
    if (currentIdx === null) return;
    const a = audioRef.current;
    if (a && a.currentTime > 3) { a.currentTime = 0; return; }
    playAtIdx(Math.max(0, currentIdx - 1));
  }, [currentIdx, playAtIdx]);

  const playNext = useCallback(() => {
    if (currentIdx === null) return;
    playAtIdx(Math.min(versesRef.current.length - 1, currentIdx + 1));
  }, [currentIdx, playAtIdx]);

  const seek = useCallback((ratio) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    a.currentTime = ratio * a.duration;
    setProgress(ratio);
  }, []);

  const cycleRepeat = useCallback(() => {
    setRepeatMode(prev => REPEAT_MODES[(REPEAT_MODES.indexOf(prev) + 1) % REPEAT_MODES.length]);
  }, []);

  const cycleSpeed = useCallback(() => {
    setSpeed(prev => {
      const next = SPEEDS[(SPEEDS.indexOf(prev) + 1) % SPEEDS.length];
      if (audioRef.current) audioRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
    stopProgress();
    setPlaying(false); setCurrentIdx(null); setProgress(0); setElapsed(0);
    setExiting(true);
    setTimeout(() => { setVisible(false); setExiting(false); }, 320);
  }, [stopProgress]);

  useEffect(() => () => { audioRef.current?.pause(); clearInterval(progressInterval.current); }, []);

  const currentVerse = currentIdx !== null ? versesRef.current[currentIdx] : null;

  // ── Media Session integration ──────────────────────────────────────────────
  const verseLabel = currentVerse
    ? `${chapterNameRef.current || 'Quran'} — ${currentVerse.verse_key}`
    : '';
  const reciterName = RECITERS[reciterId]?.name || 'Mishary Alafasy';

  useMediaSession({
    title:   verseLabel,
    artist:  reciterName,
    album:   chapterNameRef.current || 'Holy Quran',
    playing,
    duration,
    elapsed,
    onPlay:  () => audioRef.current?.play().catch(() => {}),
    onPause: () => audioRef.current?.pause(),
    onPrev:  playPrev,
    onNext:  playNext,
    onSeek:  seek,
  });

  return {
    currentVerse, currentIdx, playing, loading, progress, duration, elapsed,
    repeatMode, speed, visible, exiting, isOffline,
    playVerse, playSurah, togglePlay, playPrev, playNext, seek,
    cycleRepeat, cycleSpeed, stop,
    totalVerses: versesRef.current.length,
  };
};

export default useFullAudioPlayer;
